import { useEffect } from "react";

/**
 * Lightweight scroll-reveal using IntersectionObserver.
 * No dependency, no re-render. Elements with the `.reveal` class
 * receive `.is-visible` when they enter the viewport (once).
 *
 * Mobile-friendly: rootMargin is generous so animations start before
 * the element is fully visible, feeling snappy on small screens.
 * Respects `prefers-reduced-motion` via the CSS rules already in index.css.
 */
export const useScrollReveal = () => {
  useEffect(() => {
    if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
      // Fallback: reveal everything at once
      document
        .querySelectorAll(".reveal")
        .forEach((el) => el.classList.add("is-visible"));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 },
    );

    const observe = () => {
      document
        .querySelectorAll(".reveal:not(.is-visible)")
        .forEach((el) => io.observe(el));
    };
    observe();

    // Re-scan on route/content changes (safety net for i18n/re-renders).
    const mo = new MutationObserver(() => observe());
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      io.disconnect();
      mo.disconnect();
    };
  }, []);
};
